// ระบบติดตามนักเรียนภาคศาสนา / Early Warning
//
// รวมสัญญาณที่ไหลเข้าระบบอยู่แล้วตลอดภาค — การเช็คชื่อภาคศาสนา (is_attendance),
// ความประพฤติ (tb_conduct_record/tb_conduct_merit), เรื่องที่ครูแจ้งดูแล
// (tb_student_concern) และสุขภาพ — มาจัดอันดับความเสี่ยงรายคน โดยไม่ต้องรอคะแนนออก
//
// ไม่มีแกน "การเรียน" แบบภาคสามัญ เพราะภาคศาสนาไม่มีคะแนนเก็บรายชิ้น มีแค่คะแนนรวม
// ภาคละหนึ่งช่องที่กรอกตอนปลายภาค (ดูหมายเหตุใน server/models/islamicTrackingModel.js)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap'
import io from 'socket.io-client'
import Swal from 'sweetalert2'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  LevelBadge, InfoTip, StudentAvatar, TONE_HEX, tint, riskLabel, levelSuffix,
  useIslamicTrackingRole, useDarkTables, alertRequestError,
} from './trackingShared'

const API = process.env.REACT_APP_API
const FILTERS_KEY = 'islamicTracking.filters'

const escHtml = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const getSavedFilters = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTERS_KEY) || '{}') } catch { return {} }
}

// หนึ่งบรรทัดย่อยในการ์ดสรุป: ไอคอน + ชื่อแกน + จำนวน + แถบสัดส่วนของระดับนั้น
// ชิปใช้สีเต็มเฉพาะไอคอน/แถบ ส่วนตัวอักษรคงสีตามธีม จึงอ่านได้ทั้งโหมดสว่างและมืด
const AxisStat = ({ icon, label, value, total, tone = '#6e7985' }) => (
  <div className="mt-2">
    <div className="d-flex align-items-center gap-2 fs-xs lh-1">
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 18, height: 18, background: tint(tone, 0.16), color: tone }}
      >
        <i className={icon} style={{ fontSize: 11 }} />
      </span>
      <span className="text-truncate flex-grow-1">{label}</span>
      <span className="fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
    <div className="rounded-pill mt-1" style={{ height: 3, background: tint(tone, 0.16) }}>
      <div className="rounded-pill h-100" style={{ width: `${total > 0 ? Math.round((value / total) * 100) : 0}%`, background: tone }} />
    </div>
  </div>
)

// ป้ายข้อมูลห้อง (ใช้ในการ์ด) — label เล็กด้านบน + ค่าด้านล่าง
const MetaChip = ({ label, children }) => (
  <div className="text-center px-2">
    <div className="fs-xs text-secondary">{label}</div>
    <div className="fs-sm fw-semibold">{children}</div>
  </div>
)

// สัญญาณ 4 ช่องที่ใช้ทั้งในตารางและในการ์ด — เขียนที่เดียวเพื่อไม่ให้สองมุมมองเพี้ยนกัน
const AxisCells = ({ t, s, as = 'block' }) => {
  const cells = [
    {
      key: 'attendance', label: t.tracking.colAttendance,
      main: <LevelBadge level={s.attendance.level}>{t.tracking.absentShort(s.attendance.absent, s.attendance.absenceRate)}</LevelBadge>,
      sub: t.tracking.lateLeave(s.attendance.late, s.attendance.leave),
    },
    {
      key: 'behavior', label: t.tracking.colBehavior,
      main: <LevelBadge level={s.behavior.level}>{t.tracking.pointsLeft(s.behavior.remaining)}</LevelBadge>,
      sub: Number(s.behavior.merited || 0) > 0 ? `${t.trackingDetail.conductMerit} ${s.behavior.merited}` : null,
    },
    {
      key: 'concern', label: t.tracking.colConcern,
      main: Number(s.concern?.open || 0) > 0
        ? <LevelBadge level={s.concern.level}>{t.tracking.concernCount(s.concern.open)}</LevelBadge>
        : <span className="text-secondary">-</span>,
    },
    {
      key: 'health', label: t.tracking.colHealth,
      main: (
        <span>
          {s.health.bmiFlag && <InfoTip text={t.tracking.bmiOut(s.health.bmi)} />}
          {s.health.healthFlag
            ? <i className="ri-heart-pulse-line text-danger" title={t.tracking.hasHealthInfo} />
            : (!s.health.bmiFlag && <span className="text-secondary">-</span>)}
        </span>
      ),
    },
  ]
  if (as === 'cells') {
    return cells.map(c => (
      <td key={c.key} className="text-center">
        {c.main}
        {c.sub && <div className="fs-xs text-secondary mt-1">{c.sub}</div>}
      </td>
    ))
  }
  return (
    <div className="d-flex flex-wrap gap-2">
      {cells.map(c => (
        <div key={c.key}>
          <div className="fs-xs text-secondary mb-1">{c.label}</div>
          {c.main}
          {c.sub && <div className="fs-xs text-secondary mt-1">{c.sub}</div>}
        </div>
      ))}
    </div>
  )
}

export default function IslamicStudentTracking() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  const tt = t.tracking
  const navigate = useNavigate()
  const { roleLabel } = useIslamicTrackingRole(t)
  useDarkTables()

  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null)
  const [yearList, setYearList] = useState([])
  const [classList, setClassList] = useState([])
  const [roomList, setRoomList] = useState([])
  const [scope, setScope] = useState({ levelKeys: [] })

  const [gender, setGender] = useState(() => getSavedFilters().gender ?? '')      // '' | 'm' | 'f' (is_room.ir_gender)
  const [classId, setClassId] = useState(() => getSavedFilters().classId ?? '')
  const [roomId, setRoomId] = useState(() => getSavedFilters().roomId ?? '')
  const [minRisk, setMinRisk] = useState(() => getSavedFilters().minRisk ?? 'atrisk') // เริ่มต้น: กลุ่มเสี่ยงทั้งหมด
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({ total: 0, red: 0, orange: 0, yellow: 0, green: 0, lowBehavior: 0, openConcerns: 0, noAttendance: 0, byLevel: {} })

  const AXIS_META = useMemo(() => [
    { key: 'attendance', label: t.risk.axisAttendance, icon: 'ri-calendar-close-line' },
    { key: 'behavior',   label: t.risk.axisBehavior,   icon: 'ri-shield-user-line' },
    { key: 'concern',    label: t.risk.axisConcern,    icon: 'ri-flag-2-line' },
  ], [t])

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then(res => {
        const list = res.data || []
        setYearList(list)
        let id = localStorage.getItem('yearId')
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem('yearId', id) }
        setYearId(id)
      })
      .catch(() => {})
  }, [])

  // ชั้น/ห้อง — server คืนเฉพาะที่ผู้ใช้คนนี้มีสิทธิ์เห็น (ครูประจำชั้นได้เฉพาะห้องที่ตน
  // เป็นที่ปรึกษา หัวหน้าระดับได้เฉพาะระดับของตน) ใช้ scope เดียวกับตารางความเสี่ยง
  useEffect(() => {
    if (!yearId) return
    axiosInstance.get(`${API}/islamic/tracking/filterOptions/${yearId}`)
      .then(res => {
        setClassList(res.data?.classes || [])
        setRoomList(res.data?.rooms || [])
        setScope(res.data?.scope || { levelKeys: [] })
      })
      .catch(() => {})
  }, [yearId])

  // ตัวกรองที่จำไว้อาจชี้ไปชั้น/ห้องที่ผู้ใช้คนนี้ไม่มีสิทธิ์เห็น (เปลี่ยนตำแหน่ง/ใช้เครื่อง
  // ร่วมกัน) — ล้างทิ้ง เพื่อไม่ให้ตารางว่างโดยไม่รู้สาเหตุ
  useEffect(() => {
    if (classList.length && classId && !classList.some(c => String(c.ic_id) === String(classId))) setClassId('')
  }, [classList, classId])
  useEffect(() => {
    if (roomList.length && roomId && !roomList.some(r => String(r.ir_id) === String(roomId))) setRoomId('')
  }, [roomList, roomId])
  useEffect(() => {
    if (!gender || !roomId || !roomList.length) return
    const r = roomList.find(x => String(x.ir_id) === String(roomId))
    if (r && r.ir_gender !== gender) setRoomId('')
  }, [gender, roomId, roomList])

  // ล้างห้องเมื่อเปลี่ยนชั้น แต่ข้ามรอบแรก เพื่อไม่ให้ทับค่าห้องที่คืนมาจาก sessionStorage
  const isFirstClassRender = useRef(true)
  useEffect(() => {
    if (isFirstClassRender.current) { isFirstClassRender.current = false; return }
    setRoomId('')
  }, [classId])

  useEffect(() => {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify({ gender, classId, roomId, minRisk }))
  }, [gender, classId, roomId, minRisk])

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    if (classId) p.append('classId', classId)
    if (roomId) p.append('roomId', roomId)
    if (gender) p.append('gender', gender)
    if (minRisk) p.append('minRisk', minRisk)
    return p.toString()
  }, [classId, roomId, gender, minRisk])

  const fetchDataRef = useRef(null)
  const fetchData = useCallback(() => {
    if (!yearId) return
    setLoading(true)
    Promise.allSettled([
      axiosInstance.get(`${API}/islamic/tracking/riskList/${yearId}?${queryString}`),
      axiosInstance.get(`${API}/islamic/tracking/summary/${yearId}?${queryString}`),
    ]).then(([listRes, sumRes]) => {
      if (listRes.status === 'fulfilled') setRows(listRes.value.data || [])
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data || {})
      const failed = [listRes, sumRes].find(r => r.status === 'rejected')
      if (failed) {
        alertRequestError(t, failed.reason, t.careTracking.loadError, { retry: true })
          .then(result => { if (result.isConfirmed && failed.reason?.response?.status === 503) fetchDataRef.current?.() })
      }
    }).finally(() => setLoading(false))
  }, [yearId, queryString, setLoading, t])

  useEffect(() => { fetchDataRef.current = fetchData; fetchData() }, [fetchData])

  // อัปเดตเรียลไทม์เมื่อมีการเช็คชื่อ (ใช้ event เดิมของระบบเช็คชื่อ)
  useEffect(() => {
    if (!yearId) return
    const socket = io(process.env.REACT_APP_SOCKET_URL)
    socket.on('refreshGeneralAttendance', () => fetchDataRef.current?.())
    return () => socket.disconnect()
  }, [yearId])

  const filteredRooms = useMemo(
    () => roomList.filter(r =>
      (!classId || String(r.ic_id) === String(classId)) &&
      (!gender || r.ir_gender === gender)),
    [roomList, classId, gender]
  )

  const goToStudent = useCallback((stId) => {
    navigate(`/islamic/student-tracking/${stId}?yearId=${yearId}`)
  }, [navigate, yearId])

  // ข้อมูลย่อยในการ์ดระดับความเสี่ยง — นักเรียนหนึ่งคนอาจนับได้หลายแกน เพราะนับทุกแกน
  // ที่อยู่ที่ระดับเดียวกับระดับรวมของเขา (ตอบคำถามว่า "เสี่ยงเพราะอะไร")
  const axisBreakdown = (level) => {
    const axes = summary.byLevel?.[level] || {}
    return AXIS_META.map(a => ({ ...a, value: Number(axes[a.key] || 0) })).filter(a => a.value > 0)
  }

  const summaryCards = [
    { key: 'red',    label: tt.cardHighRisk,    value: summary.red,    bg: 'danger',    icon: 'ri-alarm-warning-line', sub: axisBreakdown('red') },
    { key: 'orange', label: tt.cardRisk,        value: summary.orange, bg: 'orange',    icon: 'ri-error-warning-line', sub: axisBreakdown('orange') },
    { key: 'yellow', label: tt.cardWatch,       value: summary.yellow, bg: 'warning',   icon: 'ri-eye-line',           sub: axisBreakdown('yellow') },
    { key: 'beh',    label: tt.cardLowBehavior, value: summary.lowBehavior,  bg: 'secondary', icon: 'ri-shield-user-line' },
    { key: 'care',   label: tt.cardConcern,     value: summary.openConcerns, bg: 'primary',   icon: 'ri-flag-2-line' },
  ]

  const yearLabel = yearList.find(y => String(y.year_id) === String(yearId))?.year_year || ''

  // พิมพ์ตารางจัดอันดับตามตัวกรองปัจจุบัน — A4 แนวนอน เปิดหน้าต่างใหม่แล้วสั่งพิมพ์
  // เอกสารที่พิมพ์เข้าแฟ้มนักเรียนเป็นภาษาไทยเสมอ (เหมือนหนังสือสัญญา) ไม่ตามภาษาเมนู
  const printRanking = () => {
    if (!rows.length) return
    const classLabel = classId
      ? (classList.find(c => String(c.ic_id) === String(classId))?.ic_class || classId)
      : th.tracking.printClassAll
    const room = roomId ? roomList.find(r => String(r.ir_id) === String(roomId)) : null
    const genderLabel = { m: th.common.male, f: th.common.female }[gender] || th.tracking.printGenderAll
    const riskText = { red: th.risk.red, orange: th.risk.orange, yellow: th.risk.yellow, atrisk: `${th.risk.red} + ${th.risk.orange} + ${th.risk.yellow}` }[minRisk] || th.tracking.riskAll
    const conditions = [
      genderLabel,
      classLabel,
      room ? `${room.ic_class}/${room.ir_no}` : th.tracking.printRoomAll,
      riskText,
    ].join(' | ')

    const counts = rows.reduce((acc, r) => { acc[r.overall] = (acc[r.overall] || 0) + 1; return acc }, {})
    const healthText = (s) => [
      s.health?.bmiFlag ? `BMI ${s.health.bmi}` : null,
      s.health?.healthFlag ? th.tracking.hasHealthInfo : null,
    ].filter(Boolean).join(', ') || '-'

    const body = rows.map((s, idx) => `<tr>
        <td class="c">${idx + 1}</td>
        <td class="c">${escHtml(s.st_idnumber)}</td>
        <td>${escHtml(s.st_name)}</td>
        <td class="c">${escHtml(s.class_room)}</td>
        <td class="c">${escHtml(s.regular_room || '-')}</td>
        <td class="c">${escHtml(s.dorm ? [s.dorm.dorm, s.dorm.village].filter(Boolean).join(' ') : '-')}</td>
        <td class="c">${escHtml(th.risk[s.overall] || '-')}</td>
        <td class="c">ขาด ${s.attendance.absent} (${s.attendance.absenceRate}%)<div class="sub">สาย ${s.attendance.late} · ลา ${s.attendance.leave}</div></td>
        <td class="c">${s.behavior.remaining} คะแนน<div class="sub">หัก ${s.behavior.deducted}${Number(s.behavior.merited || 0) > 0 ? ` · คืน ${s.behavior.merited}` : ''}</div></td>
        <td class="c">${Number(s.concern?.open || 0) > 0 ? `${s.concern.open} เรื่อง` : '-'}</td>
        <td class="c">${escHtml(healthText(s))}</td>
      </tr>`).join('')

    const title = `${th.tracking.printTitle}${yearLabel ? ` ${th.common.studyYear(yearLabel)}` : ''}`
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>${escHtml(title)}</title>
      <style>
        @page { size: A4 landscape; margin: 8mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: 'Cordia New', 'TH Sarabun New', Tahoma, Arial, sans-serif; font-size: 14px; color: #000; }
        h3, h4 { text-align: center; margin: 0 0 4px 0; }
        h3 { font-size: 18px; }
        h4 { font-size: 15px; font-weight: normal; }
        .meta { font-size: 12px; margin: 6px 0; display: flex; justify-content: space-between; }
        table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        th, td { border: 1px solid #000; padding: 2px 3px; overflow: hidden; word-wrap: break-word; }
        thead { display: table-header-group; }
        thead th { text-align: center; font-size: 12px; background: #f0f0f0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        tbody tr { page-break-inside: avoid; }
        .c { text-align: center; }
        .sub { font-size: 11px; color: #444; }
        .sign { margin-top: 14px; font-size: 12px; text-align: right; }
      </style></head>
      <body onload="window.print()">
        <h3>โรงเรียนวัฒนธรรมอิสลาม</h3>
        <h4>${escHtml(title)}</h4>
        <div class="meta">
          <span>${th.tracking.printConditions}: ${escHtml(conditions)}</span>
          <span>${escHtml(th.tracking.printSummary(rows.length, counts.red || 0, counts.orange || 0, counts.yellow || 0, counts.green || 0))}</span>
        </div>
        <table>
          <colgroup>
            <col style="width:4%;" /><col style="width:9%;" /><col style="width:20%;" /><col style="width:8%;" />
            <col style="width:8%;" /><col style="width:12%;" /><col style="width:8%;" /><col style="width:13%;" />
            <col style="width:11%;" /><col style="width:7%;" /><col style="width:8%;" />
          </colgroup>
          <thead><tr>
            <th>#</th><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ห้องศาสนา</th><th>ห้องสามัญ</th><th>หอพัก</th>
            <th>ความเสี่ยง</th><th>เข้าเรียน</th><th>ความประพฤติ</th><th>แจ้งดูแล</th><th>สุขภาพ</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
        <div class="sign">${th.tracking.printAt} ${new Date().toLocaleString('th-TH')}</div>
      </body></html>`

    const win = window.open('', '_blank', 'width=1200,height=900')
    if (!win) return Swal.fire(tt.printBlocked, tt.printBlockedText, 'warning')
    win.document.write(html)
    win.document.close()
  }

  return (
    <React.Fragment>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <div className="fs-sm text-secondary mb-1">{roleLabel} · {tt.breadcrumb}</div>
          <h4 className="main-title mb-0">
            <i className="ri-radar-line me-2 text-primary" />
            {tt.pageTitle}{levelSuffix(t, scope.levelKeys)}
          </h4>
          <p className="text-secondary fs-sm mb-0">{tt.pageSubtitle}</p>
        </div>
        <select
          className="form-select w-auto"
          value={yearId || ''}
          onChange={(e) => { localStorage.setItem('yearId', e.target.value); setYearId(e.target.value) }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      {/* การ์ดสรุป */}
      <Row className="g-3 mb-4">
        {summaryCards.map(c => (
          <Col key={c.key} xs={6} md={4} xl>
            <Card className="card-one h-100">
              <Card.Body className="py-3">
                <div className="d-flex align-items-center gap-3">
                  <div className={`d-flex align-items-center justify-content-center rounded bg-${c.bg} text-white flex-shrink-0`} style={{ width: 44, height: 44 }}>
                    <i className={`${c.icon} fs-4`} />
                  </div>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <h3 className="mb-0">{c.value ?? 0}</h3>
                    <span className="text-secondary fs-sm">{c.label}</span>
                  </div>
                </div>
                {c.sub && (
                  <div className="mt-3 pt-2 border-top">
                    <div className="fs-xs text-secondary d-flex align-items-center lh-1">
                      <span>{tt.breakdownTitle}</span>
                      <InfoTip text={tt.breakdownHelp} />
                    </div>
                    {c.sub.length
                      ? c.sub.map(a => (
                          <AxisStat key={a.key} icon={a.icon} label={a.label} value={a.value} total={c.value ?? 0} tone={TONE_HEX[c.bg]} />
                        ))
                      : <div className="fs-xs text-secondary mt-2">{tt.breakdownEmpty}</div>}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ยังเช็คชื่อไม่พอจะตัดสินแกนการเข้าเรียน — บอกไว้ไม่ให้เข้าใจว่า "ไม่มีใครเสี่ยง" */}
      {Number(summary.noAttendance || 0) > 0 && (
        <div className="alert alert-warning py-2 fs-sm d-flex align-items-start gap-2">
          <i className="ri-information-line mt-1" />
          <span>{tt.noAttendanceWarn(summary.noAttendance)}</span>
        </div>
      )}

      {/* ตัวกรอง */}
      <Card className="card-one mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col xs={6} md={3}>
              <Form.Label className="fs-sm mb-1">{tt.filterGender}</Form.Label>
              <Form.Select size="sm" value={gender} onChange={e => setGender(e.target.value)}>
                <option value="">{tt.filterAll}</option>
                <option value="m">{t.common.male}</option>
                <option value="f">{t.common.female}</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="fs-sm mb-1">{tt.filterClass}</Form.Label>
              <Form.Select size="sm" value={classId} onChange={e => setClassId(e.target.value)}>
                <option value="">{tt.filterAll}</option>
                {classList.map(c => <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-sm mb-1">{tt.filterRoom}</Form.Label>
              <Form.Select size="sm" value={roomId} onChange={e => setRoomId(e.target.value)} disabled={!classId}>
                <option value="">{tt.filterAll}</option>
                {filteredRooms.map(r => <option key={r.ir_id} value={r.ir_id}>{r.ic_class}/{r.ir_no}</option>)}
              </Form.Select>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="fs-sm mb-1">{tt.filterRisk}</Form.Label>
              <div className="btn-group btn-group-sm w-100">
                {[['red', t.risk.red], ['orange', t.risk.orange], ['yellow', t.risk.yellow], ['atrisk', tt.riskAll]].map(([v, label]) => (
                  <button key={v} type="button" className={`btn btn${minRisk === v ? '' : '-outline'}-primary`} onClick={() => setMinRisk(v)}>{label}</button>
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ตารางจัดอันดับ */}
      <Card className="card-one">
        <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">{tt.listTitle}</Card.Title>
            <Badge bg="light" text="dark" className="fs-sm">{tt.found(rows.length)}</Badge>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="fs-xs text-secondary d-none d-lg-inline">{tt.clickRowHint}</span>
            <Button size="sm" variant="outline-primary" disabled={!rows.length} onClick={printRanking}>
              <i className="ri-printer-line me-1" />{tt.printBtn}
            </Button>
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`} onClick={() => setView('table')} title={tt.viewTable}>
                <i className="ri-table-line" />
              </button>
              <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`} onClick={() => setView('card')} title={tt.viewCard}>
                <i className="ri-layout-grid-line" />
              </button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {rows.length === 0 ? (
            <div className="text-center text-secondary py-5">
              <i className="ri-shield-check-line fs-2 d-block mb-2" />
              {tt.empty}
            </div>
          ) : view === 'card' ? (
            <div style={{ maxHeight: 640, overflowY: 'auto' }} className="p-3">
              <Row className="g-3">
                {rows.map((s, idx) => (
                  <Col key={s.st_id} xs={12} md={6} xl={4}>
                    <div className="border rounded p-3 shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => goToStudent(s.st_id)}>
                      <div className="d-flex align-items-start gap-2">
                        <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} size={40} />
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="fw-semibold text-truncate"><StudentName student={s} lang={lang} /></div>
                            <LevelBadge level={s.overall}>{riskLabel(t, s.overall)}</LevelBadge>
                          </div>
                          <div className="fs-xs text-secondary">#{idx + 1} · {s.st_idnumber}</div>
                        </div>
                      </div>
                      <div className="d-flex flex-wrap justify-content-start gap-1 mt-2 pt-2 border-top">
                        <MetaChip label={tt.colRoom}>{s.class_room}</MetaChip>
                        <MetaChip label={tt.colRegularRoom}>{s.regular_room || <span className="text-secondary">-</span>}</MetaChip>
                        <MetaChip label={tt.colDorm}>
                          {s.dorm
                            ? <>{s.dorm.dorm}{s.dorm.village && <div className="fs-xs text-secondary fw-normal">{s.dorm.village}</div>}</>
                            : <span className="text-secondary">-</span>}
                        </MetaChip>
                      </div>
                      <div className="mt-2 pt-2 border-top">
                        <AxisCells t={t} s={s} />
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            <div style={{ maxHeight: 640, overflowY: 'auto' }}>
              <Table responsive hover size="sm" className="mb-0 align-middle">
                <thead className="table-secondary sticky-top">
                  <tr>
                    <th className="text-center" style={{ width: 44 }}>{tt.colNo}</th>
                    <th style={{ minWidth: 200 }}>{tt.colStudent}</th>
                    <th className="text-center" style={{ width: 80 }}>{tt.colRoom}</th>
                    <th className="text-center" style={{ width: 80 }}>{tt.colRegularRoom}</th>
                    <th className="text-center" style={{ minWidth: 110 }}>{tt.colDorm}</th>
                    <th className="text-center" style={{ width: 110 }}>{tt.colOverall}<InfoTip text={tt.tipOverall} /></th>
                    <th className="text-center" style={{ width: 150 }}>{tt.colAttendance}<InfoTip text={tt.tipAttendance} /></th>
                    <th className="text-center" style={{ width: 120 }}>{tt.colBehavior}<InfoTip text={tt.tipBehavior} /></th>
                    <th className="text-center" style={{ width: 90 }}>{tt.colConcern}<InfoTip text={tt.tipConcern} /></th>
                    <th className="text-center" style={{ width: 70 }}>{tt.colHealth}<InfoTip text={tt.tipHealth} /></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s, idx) => (
                    <tr key={s.st_id} style={{ cursor: 'pointer' }} onClick={() => goToStudent(s.st_id)}>
                      <td className="text-center text-secondary">{idx + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} />
                          <div>
                            <div className="fw-semibold"><StudentName student={s} lang={lang} /></div>
                            <div className="fs-xs text-secondary">{s.st_idnumber}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center fs-sm">{s.class_room}</td>
                      <td className="text-center fs-sm">{s.regular_room || <span className="text-secondary">-</span>}</td>
                      <td className="text-center fs-sm">
                        {s.dorm
                          ? <>
                              <div>{s.dorm.dorm}</div>
                              {s.dorm.village && <div className="fs-xs text-secondary">{s.dorm.village}</div>}
                            </>
                          : <span className="text-secondary">-</span>}
                      </td>
                      <td className="text-center"><LevelBadge level={s.overall}>{riskLabel(t, s.overall)}</LevelBadge></td>
                      <AxisCells t={t} s={s} as="cells" />
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </React.Fragment>
  )
}
